// Account-Level Negative Coverage — Negatives section, deterministic check.
// Shows shared-list inventory + per-campaign coverage, sorted with uncovered campaigns
// first so the actionable rows are at the top.

import classNames from 'classnames';

import type { EvidenceProps } from './registry';
import { pct } from '../../utils/format';

interface SharedListRow {
  id?: string;
  name?: string;
  memberCount: number;
}

interface CampaignCoverageRow {
  campaignId?: string;
  campaignName?: string;
  attachedListIds: string[];
}

interface AccountNegativeShape {
  totalSearchCampaigns: number;
  sharedListCount: number;
  totalNegativeKeywordsInLists: number;
  campaignsCoveredCount: number;
  campaignsUncoveredCount: number;
  coveragePct: number;
  sharedLists: SharedListRow[];
  campaigns: CampaignCoverageRow[];
}

export const AccountLevelNegativeCoverageEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as AccountNegativeShape;
  // Build a quick lookup of list ID → name so the per-campaign chips render with friendly
  // names rather than opaque IDs.
  const listNameById = new Map(e.sharedLists.map(l => [l.id ?? '', l.name ?? '(unnamed)']));

  return (
    <div className='evidence'>
      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Coverage at a glance
          <span className='evidence__section-tag evidence__section-tag--context'>
            {e.totalSearchCampaigns} Search campaigns
          </span>
        </h4>
        <div className='audience-coverage'>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{pct(e.coveragePct)}</div>
            <div className='audience-coverage__lbl'>Campaign coverage</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{e.sharedListCount.toLocaleString()}</div>
            <div className='audience-coverage__lbl'>Shared lists</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>
              {e.totalNegativeKeywordsInLists.toLocaleString()}
            </div>
            <div className='audience-coverage__lbl'>Negatives in lists</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>
              {e.campaignsUncoveredCount.toLocaleString()}
            </div>
            <div className='audience-coverage__lbl'>Uncovered campaigns</div>
          </div>
        </div>
      </section>

      {e.sharedLists.length > 0 && (
        <section className='evidence__section'>
          <h4 className='evidence__section-title'>
            Shared negative lists on this account
            <span className='evidence__section-tag evidence__section-tag--context'>
              {e.sharedLists.length} lists
            </span>
          </h4>
          <table className='evidence__table evidence__table--shared-lists'>
            <thead>
              <tr>
                <th>List name</th>
                <th className='evidence__th--right'>Negative keywords</th>
              </tr>
            </thead>
            <tbody>
              {e.sharedLists.map(l => (
                <tr key={l.id ?? l.name}>
                  <td>{l.name ?? '(unnamed)'}</td>
                  <td className='evidence__td--right'>{l.memberCount.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Per-campaign attachment
          <span className='evidence__section-tag evidence__section-tag--context'>
            {e.totalSearchCampaigns} campaigns
          </span>
        </h4>
        <table className='evidence__table evidence__table--negative-coverage'>
          <thead>
            <tr>
              <th>Campaign</th>
              <th className='evidence__th--right'>Attached lists</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            {e.campaigns.map(c => {
              const uncovered = c.attachedListIds.length === 0;
              return (
                <tr
                  key={c.campaignId ?? c.campaignName}
                  className={classNames({ 'evidence__row--flagged': uncovered })}
                >
                  <td>{c.campaignName ?? '(unnamed)'}</td>
                  <td className='evidence__td--right'>
                    {c.attachedListIds.length.toLocaleString()}
                  </td>
                  <td>
                    {uncovered ? (
                      <span className='evidence__inline-note'>No shared-list attachment</span>
                    ) : (
                      <span className='evidence__modifier-list'>
                        {c.attachedListIds.map(listId => (
                          <span key={listId} className='evidence__audience-chip'>
                            {listNameById.get(listId) ?? listId}
                          </span>
                        ))}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      <p className='evidence__footnote'>
        Shared negative-keyword lists are the account-level infrastructure for applying standard
        exclusions (job-seeker terms, brand misspellings to exclude from non-brand, competitor
        terms, etc.) consistently across many campaigns. Per-campaign negatives still matter, but
        shared lists are the modern starting point — they centralize maintenance and prevent drift.
      </p>
    </div>
  );
};
